import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { BOOK_DEMO_URL } from "../../lib/motion";

const LINKS = [
  { label: "About", to: "/about" },
  { label: "ForesightFlow", to: "/foresightflow" },
  { label: "Team", to: "/team" },
  { label: "Blogs", to: "/blogs" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div
        className={`transition-[background-color,backdrop-filter,border-color] duration-500 border-b ${
          scrolled
            ? "bg-[#FAF8F5]/85 backdrop-blur-md border-[rgba(28,28,30,0.08)]"
            : "bg-transparent border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            {/* Wordmark / logo */}
            <Link to="/" data-testid="nav-logo" className="flex items-center group">
              <img
                src="/assets/jsc-logo.webp"
                alt="Jay Shah Consultancy"
                className="h-11 md:h-12 w-auto object-contain mix-blend-multiply"
              />
            </Link>

            {/* Center links */}
            <nav className="hidden md:flex items-center gap-10">
              {LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={`link-underline font-body text-sm transition-colors duration-300 ${
                    pathname === l.to ? "text-[#1C1C1E] font-medium" : "text-[#1C1C1E]/70 hover:text-[#1C1C1E]"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            {/* CTA */}
            <div className="flex items-center gap-3">
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-book-demo"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#B39B77] px-5 py-2.5 font-body text-sm font-medium text-[#1C1C1E] transition-colors duration-300 hover:bg-[#96805E]"
              >
                Book a Demo <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
              </a>
              <button
                onClick={() => setOpen((v) => !v)}
                data-testid="nav-mobile-toggle"
                className="md:hidden text-[#1C1C1E] p-1"
                aria-label="Toggle menu"
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[rgba(28,28,30,0.08)]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-display text-2xl text-[#1C1C1E]"
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-book-demo"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#B39B77] px-5 py-3 font-body text-sm font-medium text-[#1C1C1E]"
              >
                Book a Demo <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
